import { generateRandomId } from "@/react/helpers/generateRandomId";
import type {
  BadgeProperty,
  BenefitProperty,
  PaywallCustomization,
  TestimonialProperty,
} from "@/react/types/PaywallCustomization";

const addIds = <T>(input: T): T => ({
  id: generateRandomId(),
  ...input,
});

const serializeBenefitProperties = (
  benefitProperties?: PaywallCustomization["benefit_properties"],
): PaywallCustomization["benefit_properties"] => {
  if (!benefitProperties) {
    return { enabled: false, title: "", benefits: [] };
  }

  return {
    ...benefitProperties,
    benefits: (benefitProperties.benefits || []).map(addIds<BenefitProperty>),
  };
};

const serializeTestimonialProperties = (
  testimonialProperties?: PaywallCustomization["testimonial_properties"],
): PaywallCustomization["testimonial_properties"] => {
  if (!testimonialProperties) {
    return { enabled: false, testimonials: [] };
  }

  return {
    ...testimonialProperties,
    testimonials: (testimonialProperties.testimonials || []).map(
      addIds<TestimonialProperty>,
    ),
  };
};

const serializeBadgeProperties = (
  badgeProperties?: PaywallCustomization["badge_properties"],
): PaywallCustomization["badge_properties"] => {
  if (!badgeProperties) {
    return { enabled: false, badges: [] };
  }

  return {
    ...badgeProperties,
    badges: (badgeProperties.badges || []).map(addIds<BadgeProperty>),
  };
};

export const usePaywallCustomization = (
  paywallCustomization?: PaywallCustomization,
): undefined | PaywallCustomization => {
  if (!paywallCustomization) {
    return undefined;
  }

  return {
    ...paywallCustomization,
    badge_properties: serializeBadgeProperties(
      paywallCustomization.badge_properties,
    ),
    testimonial_properties: serializeTestimonialProperties(
      paywallCustomization.testimonial_properties,
    ),
    benefit_properties: serializeBenefitProperties(
      paywallCustomization.benefit_properties,
    ),
  };
};
